import { memo, useState } from 'react'
import { GlassButton } from '@/components/ui/GlassButton'
import { Input } from '@/components/ui/input'
import { AnimatedUnderline } from '@/components/ui/AnimatedUnderline'
import { LogIn, Zap, Search, X, ChevronDown } from 'lucide-react'
import {
  DESKTOP_NAVIGATION_ITEMS,
  NAVIGATION_ITEMS,
} from '@/constants/navigation'
import { useSearch } from '@/hooks/useSearch'

interface HeaderNavProps {
  isMenuOpen: boolean
  onToggleMenu?: () => void
}

const HeaderNav = memo(({ isMenuOpen, onToggleMenu }: HeaderNavProps) => {
  const { setQuery, handleSearch, suggestions } = useSearch()
  const [mobileSearchQuery, setMobileSearchQuery] = useState('')
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const handleMobileSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (mobileSearchQuery.trim()) {
      setQuery(mobileSearchQuery)
      handleSearch(mobileSearchQuery)
      onToggleMenu?.()
    }
  }

  return (
    <>
      {/* Tablet Navigation */}
      <nav className="hidden md:flex xl:hidden items-center space-x-6">
        {DESKTOP_NAVIGATION_ITEMS.slice(0, 3).map(item => (
          <AnimatedUnderline
            key={item.href}
            href={item.href}
            className="text-sm font-medium"
          >
            {item.label}
          </AnimatedUnderline>
        ))}
      </nav>

      {/* Desktop Navigation */}
      <nav className="hidden xl:flex items-center space-x-8">
        {DESKTOP_NAVIGATION_ITEMS.map(item => (
          <div key={item.label} className="relative">
            {item.children ? (
              <div
                className="flex items-center text-sm font-medium cursor-pointer"
                onMouseEnter={() => setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <div className="relative">
                  <AnimatedUnderline className="text-sm font-medium">
                    {item.label}
                  </AnimatedUnderline>
                  <ChevronDown className="w-4 h-4 flex-shrink-0 ml-1 absolute -right-6 top-1/2 -translate-y-1/2" />
                </div>

                {/* Dropdown Menu */}
                {activeDropdown === item.label && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white/95 backdrop-blur-xl border border-gray-200/60 rounded-2xl shadow-lg z-50">
                    <div className="p-2">
                      {item.children.map(child => (
                        <a
                          key={child.href}
                          href={child.href}
                          className="flex items-center space-x-3 p-3 rounded-xl hover:bg-gray-50/80 transition-colors duration-200 group"
                        >
                          {child.icon && (
                            <child.icon className="h-4 w-4 text-gray-400 group-hover:text-brand-purple transition-colors duration-200" />
                          )}
                          <span className="text-sm font-medium text-gray-700 group-hover:text-brand-purple transition-colors duration-200">
                            {child.label}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <AnimatedUnderline
                href={item.href}
                className="text-sm font-medium"
              >
                {item.label}
              </AnimatedUnderline>
            )}
          </div>
        ))}
      </nav>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[65] xl:hidden"
          onClick={onToggleMenu}
        />
      )}

      {/* Mobile Menu - Full Screen with Scroll */}
      <div
        className={`xl:hidden fixed inset-0 z-[70] transform transition-all duration-500 ease-out ${
          isMenuOpen
            ? 'translate-y-0 opacity-100'
            : 'translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div className="h-full bg-white/95 backdrop-blur-xl border-t border-gray-200/50 shadow-2xl overflow-hidden">
          <div className="h-full flex flex-col">
            {/* Header with Search - Fixed */}
            <div className="flex-shrink-0 px-6 py-6 border-b border-gray-200/50 bg-white/95 backdrop-blur-xl">
              <div className="space-y-4">
                {/* Header with Close Button */}
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900">Menü</h2>
                  <button
                    onClick={onToggleMenu}
                    className="p-2 rounded-xl bg-gray-100/80 backdrop-blur-sm border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 hover:bg-gray-200/90 hover:scale-110 active:scale-95"
                    aria-label="Menü schließen"
                  >
                    <X className="h-5 w-5 text-gray-600 hover:text-gray-900 transition-colors duration-300" />
                  </button>
                </div>

                {/* Integrated Search */}
                <form onSubmit={handleMobileSearch} className="relative">
                  <div className="flex items-center bg-gray-50/80 backdrop-blur-sm border border-gray-200/60 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 focus-within:ring-2 focus-within:ring-brand-purple/30 focus-within:border-brand-purple/50">
                    <div className="pl-4 pr-3">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <Input
                      type="text"
                      placeholder="Produkte suchen..."
                      value={mobileSearchQuery}
                      onChange={e => setMobileSearchQuery(e.target.value)}
                      className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-gray-500 flex-1 h-12 text-gray-900"
                    />
                  </div>

                  {/* Search Suggestions */}
                  {mobileSearchQuery && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-xl border border-gray-200/60 rounded-2xl shadow-lg z-10">
                      <div className="p-2">
                        {suggestions.slice(0, 5).map(suggestion => (
                          <button
                            key={suggestion.id}
                            onClick={() => {
                              setMobileSearchQuery(suggestion.title)
                              setQuery(suggestion.title)
                              handleSearch(suggestion.title)
                              onToggleMenu?.()
                            }}
                            className="w-full text-left p-3 rounded-xl hover:bg-gray-50/80 transition-colors duration-200 group"
                          >
                            <div className="flex items-center space-x-3">
                              <Search className="h-4 w-4 text-gray-400 group-hover:text-brand-purple transition-colors duration-200" />
                              <span className="text-sm font-medium text-gray-700 group-hover:text-brand-purple transition-colors duration-200">
                                {suggestion.title}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <div className="px-6 py-6">
                {/* Navigation Items */}
                <nav className="space-y-2">
                  {NAVIGATION_ITEMS.map((item, index) => {
                    const Icon = item.icon
                    const isExpanded = expandedItems.has(item.label)

                    if (item.children) {
                      return (
                        <div key={item.label}>
                          <button
                            onClick={() => {
                              const newExpanded = new Set(expandedItems)
                              if (isExpanded) {
                                newExpanded.delete(item.label)
                              } else {
                                newExpanded.add(item.label)
                              }
                              setExpandedItems(newExpanded)
                            }}
                            className="group w-full flex items-center justify-between p-4 rounded-2xl bg-gray-50/60 backdrop-blur-sm border border-gray-200/40 shadow-sm hover:shadow-lg transition-all duration-300 hover:bg-gray-100/80 hover:border-gray-300/60 hover:scale-[1.02] active:scale-[0.98]"
                            style={{
                              animationDelay: `${index * 80}ms`,
                            }}
                          >
                            <div className="flex items-center space-x-4">
                              {/* Icon */}
                              <div className="flex-shrink-0">
                                {Icon && (
                                  <Icon className="h-6 w-6 text-gray-600 group-hover:text-brand-purple transition-colors duration-300" />
                                )}
                              </div>
                              {/* Label */}
                              <span className="text-base font-medium text-gray-700 group-hover:text-brand-purple transition-colors duration-300">
                                {item.label}
                              </span>
                            </div>
                            <ChevronDown
                              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                            />
                          </button>

                          {/* Dropdown Items */}
                          {isExpanded && (
                            <div className="ml-4 mt-2 space-y-1">
                              {item.children.map((child, childIndex) => (
                                <a
                                  key={child.href}
                                  href={child.href}
                                  onClick={onToggleMenu}
                                  className="group flex items-center space-x-4 p-3 rounded-xl bg-gray-40/40 backdrop-blur-sm border border-gray-200/20 shadow-sm hover:shadow-md transition-all duration-300 hover:bg-gray-50/60 hover:border-gray-300/40 hover:scale-[1.01] active:scale-[0.99]"
                                  style={{
                                    animationDelay: `${index * 80 + childIndex * 40}ms`,
                                  }}
                                >
                                  {/* Icon */}
                                  <div className="flex-shrink-0">
                                    {child.icon && (
                                      <child.icon className="h-5 w-5 text-gray-500 group-hover:text-brand-purple transition-colors duration-300" />
                                    )}
                                  </div>
                                  {/* Label */}
                                  <span className="text-sm font-medium text-gray-600 group-hover:text-brand-purple transition-colors duration-300">
                                    {child.label}
                                  </span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    }

                    return (
                      <a
                        key={item.href}
                        href={item.href}
                        onClick={onToggleMenu}
                        className="group flex items-center space-x-4 p-4 rounded-2xl bg-gray-50/60 backdrop-blur-sm border border-gray-200/40 shadow-sm hover:shadow-lg transition-all duration-300 hover:bg-gray-100/80 hover:border-gray-300/60 hover:scale-[1.02] active:scale-[0.98]"
                        style={{
                          animationDelay: `${index * 80}ms`,
                        }}
                      >
                        {/* Icon */}
                        <div className="flex-shrink-0">
                          {Icon && (
                            <Icon className="h-6 w-6 text-gray-600 group-hover:text-brand-purple transition-colors duration-300" />
                          )}
                        </div>

                        {/* Label */}
                        <span className="text-base font-medium text-gray-700 group-hover:text-brand-purple transition-colors duration-300">
                          {item.label}
                        </span>
                      </a>
                    )
                  })}
                </nav>

                {/* Action Buttons */}
                <div className="pt-6 space-y-3">
                  <GlassButton
                    variant="ghost"
                    size="lg"
                    onClick={onToggleMenu}
                    className="w-full justify-start h-14 px-4 group"
                  >
                    <div className="flex items-center space-x-4">
                      <LogIn className="h-6 w-6 text-gray-600 group-hover:text-brand-purple transition-colors duration-300" />
                      <span className="text-base font-medium text-gray-700 group-hover:text-brand-purple transition-colors duration-300">
                        Registrieren
                      </span>
                    </div>
                  </GlassButton>

                  <GlassButton
                    variant="primary"
                    size="lg"
                    onClick={onToggleMenu}
                    className="w-full justify-start h-14 px-4 group"
                  >
                    <div className="flex items-center space-x-4">
                      <Zap className="h-6 w-6 text-brand-purple group-hover:text-brand-purple transition-colors duration-300" />
                      <span className="text-base font-medium text-brand-purple group-hover:text-brand-purple transition-colors duration-300">
                        KI-Assistent
                      </span>
                    </div>
                  </GlassButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
})

HeaderNav.displayName = 'HeaderNav'

export default HeaderNav
